import React, { useState, useRef } from "react";
import {
  X,
  Download,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Share,
} from "lucide-react";
import ReactPlayer from "react-player";
import { toast } from "react-toastify";

const RecordingModal = ({ isOpen, onClose, recording }) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [played, setPlayed] = useState(0);
  const [seeking, setSeeking] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [showSharePopup, setShowSharePopup] = useState(false);

  const playerRef = useRef(null);

  if (!isOpen || !recording) return null;

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const handleMute = () => {
    setIsMuted(!isMuted);
  };

  const handleProgress = (state) => {
    if (!seeking) {
      setPlayed(state.played);
    }
  };

  const handleSeekMouseDown = () => {
    setSeeking(true);
  };

  const handleSeekChange = (e) => {
    setPlayed(parseFloat(e.target.value));
  };

  const handleSeekMouseUp = (e) => {
    setSeeking(false);
    playerRef.current.seekTo(parseFloat(e.target.value));
  };

  const handleDuration = (duration) => {
    setDuration(duration);
  };

  const formatTime = (seconds) => {
    const date = new Date(seconds * 1000);
    const hh = date.getUTCHours();
    const mm = date.getUTCMinutes();
    const ss = date.getUTCSeconds().toString().padStart(2, "0");
    if (hh) {
      return `${hh}:${mm.toString().padStart(2, "0")}:${ss}`;
    }
    return `${mm}:${ss}`;
  };

  const handleDownload = () => {
    console.log("Download recording:", recording.id);
    // Implement download functionality
    const link = document.createElement("a");
    link.href = recording.file.fileUrl;
    link.download = recording.title || "recording.mp4";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(
      `${process.env.REACT_APP_RECORDING_SHARE_LINK}/${recording.id}`
    );
    setShowSharePopup(false);
    toast.success("Link Copied");
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-black rounded-lg shadow-2xl w-full max-w-6xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex flex-col p-2">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">
                {recording.name || "Untitled Recording"}
              </h2>
              <p className="text-sm text-gray-200 mt-1">
                {recording.createdAt
                  ? new Date(recording.createdAt).toLocaleDateString()
                  : "N/A"}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleDownload}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Download"
              >
                <Download className="h-5 w-5 text-white" />
              </button>

              <button
                onClick={() => setShowSharePopup(!showSharePopup)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Share"
              >
                <Share className="h-5 w-5 text-white" />
              </button>

              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Close"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          </div>
          {showSharePopup && (
            <div className="absolute top-12 right-12 bg-black p-4 rounded shadow-lg z-10">
              <input
                type="text"
                value={`${process.env.REACT_APP_RECORDING_SHARE_LINK}/${recording.file.id}`}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded mb-2"
              />
              <button
                onClick={handleCopyLink}
                className="px-4 py-2 bg-[#7700FF] text-white rounded hover:bg-[#7700ff]"
              >
                Copy Link
              </button>
            </div>
          )}
        </div>

        {/* Video Player */}
        <div className="relative bg-black">
          <div className="aspect-video">
            <ReactPlayer
              ref={playerRef}
              url={recording.file.fileUrl}
              width="100%"
              height="100%"
              playing={isPlaying}
              volume={isMuted ? 0 : volume}
              playbackRate={playbackRate}
              onProgress={handleProgress}
              onDuration={handleDuration}
              controls={false}
              config={{
                file: {
                  attributes: {
                    controlsList: "nodownload",
                  },
                },
              }}
            />
          </div>

          {/* Custom Controls Overlay */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
            {/* Progress Bar (Seek Slider) */}
            <div className="mb-4">
              <input
                type="range"
                min="0"
                max="1"
                step="any"
                value={played}
                onMouseDown={handleSeekMouseDown}
                onChange={handleSeekChange}
                onMouseUp={handleSeekMouseUp}
                className="w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #fff 0%, #fff ${
                    played * 100
                  }%, #4B5563 ${played * 100}%, #4B5563 100%)`,
                }}
              />
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* Play/Pause Button */}
                <button
                  onClick={handlePlayPause}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  {isPlaying ? (
                    <Pause className="h-6 w-6 text-white" />
                  ) : (
                    <Play className="h-6 w-6 text-white" />
                  )}
                </button>

                {/* Time Display */}
                <div className="text-white text-sm font-mono">
                  {formatTime(played * duration)} / {formatTime(duration)}
                </div>
              </div>

              <div className="flex items-center space-x-4">
                {/* Volume Control */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleMute}
                    className="p-1 hover:bg-white/20 rounded transition-colors"
                  >
                    {isMuted ? (
                      <VolumeX className="h-5 w-5 text-white" />
                    ) : (
                      <Volume2 className="h-5 w-5 text-white" />
                    )}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className="w-20 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
                  />
                </div>
              </div>

              {/* Playback Speed Control */}
              <div className="flex items-center space-x-2">
                <span className="text-white text-sm">Speed:</span>
                <select
                  value={playbackRate}
                  onChange={(e) => setPlaybackRate(parseFloat(e.target.value))}
                  className="bg-gray-700 text-white text-sm rounded px-2 py-1 focus:outline-none"
                >
                  <option value={0.5}>0.5x</option>
                  <option value={0.75}>0.75x</option>
                  <option value={1.0}>1.0x</option>
                  <option value={1.25}>1.25x</option>
                  <option value={1.5}>1.5x</option>
                  <option value={2.0}>2.0x</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Recording Info */}
        <div className="p-6 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-1">
                Duration
              </h3>
              <p className="text-sm text-gray-900">{formatTime(duration)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-1">Size</h3>
              <p className="text-sm text-gray-900">
                {recording.file.size
                  ? `${(recording.file.size / (1024 * 1024)).toFixed(2)} MB`
                  : "0 MB"}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-1">Format</h3>
              <p className="text-sm text-gray-900">
                {recording.format || "MP4"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecordingModal;
